import { SpotFavoriteUnselectedIcon } from '@tableau/icons/SpotFavoriteUnselectedIcon';
import React from 'react';
import _ from 'underscore';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { Location } from '~commonInfra/utils/Location';
import { ConnectedQueryParamUpdateProvider } from '~src/bootstrap/routerHelpers/QueryParamUpdateProvider';
import { Messages } from '~src/codegen/Localize';
import { ProjectDefinition } from '~src/projects/components/contentDefinitions/ProjectDefinition';
import { ConnectedExplorer } from '~src/spacesPlatform/components/Explorer/ConnectedExplorer';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '~src/spacesPlatform/components/Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '~src/spacesPlatform/components/Explorer/FilterByTypeDropdown';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { ExplorerEmptyStateProps } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerEmpyState';
import { SpaceHeaderContent } from '~src/spacesPlatform/components/header/SpaceHeaderContent';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { getFavoriteMixedContentInOrder } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { SpaceInfo, SpaceItemForNonEntitySpace, SpaceType } from '~src/spacesPlatform/state/SpaceInfo';
import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

export interface FavoritesSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly urls: SpacesSiteUrls;
}

export interface FavoritesSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly requestSpaceContents: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly callFavoritesReorder: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export interface FavoritesSpaceOwnProps {
  readonly siteName: string;
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export type FavoritesSpaceProps = FavoritesSpaceStateProps
  & FavoritesSpaceDispatchProps
  & FavoritesSpaceOwnProps;

const FavoritesSpaceInfo: SpaceInfo = {
  item: SpaceItemForNonEntitySpace,
  spaceType: 'favorites',
  subspaceName: undefined
};

export class FavoritesSpace extends React.PureComponent<FavoritesSpaceProps> {
  public componentDidMount() {
    this.props.requestSpaceContents();
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.favoritesBrowserTitle() });
  }

  public componentDidUpdate(prevProps: FavoritesSpaceProps) {
    if(!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.props.requestSpaceContents();
    }
  }

  public render(): JSX.Element {
    return (
      <ConnectedSpaceInfoProvider spaceInfo={FavoritesSpaceInfo}>
        <ConnectedQueryParamUpdateProvider
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
          shouldUpdateBreadcrumbs={false}
          shouldUpdateContentTypes={true}
          shouldUpdateFilters={true}
          shouldUpdateSort={true}
        >
          <div className={SpaceContainerStyles.SpaceContainer}>
            <div className={SpaceContainerStyles.MastheadArea}>
              <SpaceHeaderContent headerText={Messages.favoritesBrowserTitle()} />
              <div style={SpaceContainerStyles.MastheadSubheadingArea}>
                {Messages.favoritesMastheadSubheading()}
              </div>
            </div>
            <div className={SpaceContainerStyles.SubplaceArea}>
              <ConnectedExplorer
                supportedDisplayModes={ProjectDefinition.supportedDisplayModes}
                emptyStateProps={this.emptyStateProps()}
                toolbarProps={{
                  supportFilterPanel: true,
                  toolbarContentFilterControl: this.contentFilterControl(),
                  toolbarVisibility: 'enabled'
                }}
                reorderItemAction={this.props.callFavoritesReorder}
                allowDragAndDrop={true}
                reorderAccessibilityInstructions = {Messages.favoritesReorderAccessibilityInstructions()}
              />
            </div>
          </div>
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private contentFilterControl(): JSX.Element {
    return (
      <React.Fragment>
        <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
           {Messages.filterByTypeDropdown_label()}
        </span>
        <FilterByTypeDropdown
          filterSelected={this.props.filterSelected}
          contentFilter={this.props.contentTypeFilterValues}
          defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
          clearContentCounts={this.props.clearContentCounts}
          spaceType={FavoritesSpaceInfo.spaceType}
          contentItemDefinitions={getFavoriteMixedContentInOrder()}
        />
      </React.Fragment>
    );
  }

  private emptyStateProps(): ExplorerEmptyStateProps {
    return {
      icon: SpotFavoriteUnselectedIcon,
      primaryMessage: Messages.favoritesEmptyStatePrimary(),
      secondaryMessage: Messages.favoritesEmptyStateSecondary(),
      onActionButton: this.onNoContentAction,
      actionButtonMessage: Messages.emptyStateCallToAction()
    };
  }

  private onNoContentAction = (): void => {
    Location.instance.setUrl(this.props.urls.exploreUrl());
  };
}
